'use client'
import { useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'
import { GraduationCap, Award, Briefcase, PlaneTakeoff, Calendar, ShieldCheck, ArrowRight, BadgeCheck, Users, ScrollText, Sunrise, Sunset, PhoneCall, MessageCircleQuestion, Sparkles } from 'lucide-react'
import type { Locale } from '@/lib/common-content'
import { ConsultationCTASection } from '@/components/ConsultationCTASection'
import { PageHeroEyebrow, pageHeroLeadClass, pageHeroTitleClass } from '@/components/PageHeroEyebrow'
import { PageDecor, ClassroomPhotoStrip } from '@/components/PageDecor'
import { openApply } from '@/components/apply/ApplyDialog'

/** Next.js link with framer-motion hover effects (pre-loads the target page) */
const MotionLink = motion.create(Link)

/** JLPT levels offered in the batch timings (tabs) */
const BATCH_LEVELS = ['N5', 'N4', 'N3', 'N2', 'N1'] as const

const content = {
  en: {
    label: 'Individuals Program',
    title: 'Japanese for your future in Japan',
    description: 'Learn Japanese from N5 to N1, prepare for SSW and TITP, and get help with jobs and visas, in small classes in Lahore or online.',
    highlights: [
      { icon: 'cert', text: 'N5 - N1 Certified', href: '#class-schedule' },
      { icon: 'job', text: 'Job Placement Support', href: '#programs' },
      { icon: 'visa', text: 'Visa Assistance', href: '#programs' },
    ],
    cardCta: 'Ask about this',
    firstCardCta: 'See batch timings',
    cards: [
      { title: 'Japanese from N5 to N1', desc: 'Step-by-step courses from your first hiragana to the advanced N1 level, with weekly mock tests.' },
      { title: 'SSW & TITP Preparation', desc: 'Japanese and skills-test practice for your field, plus mock interviews and the rules you need to know.' },
      { title: 'Job Placement Support', desc: 'Help with your Japanese CV, interview practice and guidance towards employers in Japan.' },
      { title: 'Visa Guidance', desc: 'We explain the visa steps, check your documents with you and help you get ready to depart.' },
      { title: 'Flexible Batches', desc: 'Morning or afternoon classes, five days a week, on campus in Lahore or online.' },
      { title: 'Personal Mentoring', desc: 'Your teacher tracks your progress every week and helps you fix weak points one-to-one.' },
    ],
    schedule: {
      label: 'Class Schedule - 2026/27 Intakes Open',
      title: 'JLPT Batch Timings',
      description: 'Pick your level, then the batch that fits your routine for the 2026/27 intakes. Five days a week, morning or afternoon.',
      badge: 'Limited Seats Only',
      levelTabLabel: 'JLPT',
      // Same two batches for every level (N5 to N1), as in the review PDF
      slots: [
        { slot: 'Morning Batch', time: '9:00 AM - 11:00 AM', icon: 'morning' },
        { slot: 'Afternoon Batch', time: '2:00 PM - 4:00 PM', icon: 'afternoon' },
      ],
      days: 'Five days a week · 2026/27 Intake',
      applyBtnLabel: 'Apply Now',
    },
    custom: {
      label: 'Something Else in Mind?',
      title: 'Looking for a Custom Plan?',
      description: "If what you need isn't listed above, reach out — we'll build a plan around your goals and schedule.",
      btnLabel: 'Contact Us',
      phone: '+92 300 1301223',
    },
  },
  ja: {
    label: '個人向けプログラム',
    title: '日本での未来のための日本語',
    description: 'N5〜N1の日本語、特定技能・技能実習の準備、就職・ビザのサポートを、ラホールまたはオンラインの少人数クラスで。',
    highlights: [
      { icon: 'cert', text: 'N5 - N1 認定', href: '#class-schedule' },
      { icon: 'job', text: '就職サポート', href: '#programs' },
      { icon: 'visa', text: 'ビザサポート', href: '#programs' },
    ],
    cardCta: 'お問い合わせ',
    firstCardCta: '時間割を見る',
    cards: [
      { title: 'N5〜N1の日本語', desc: '最初のひらがなから上級のN1まで、毎週の模擬試験付きで段階的に学べます。' },
      { title: '特定技能・技能実習の準備', desc: '分野別の日本語と技能試験の練習、模擬面接、知っておくべきルール。' },
      { title: '就職サポート', desc: '日本語の履歴書、面接練習、日本の企業へのガイド。' },
      { title: 'ビザのガイド', desc: 'ビザの手順を説明し、書類を一緒に確認し、出国準備をサポートします。' },
      { title: '選べるクラス時間', desc: '午前または午後、週5日。ラホールでの通学またはオンライン。' },
      { title: '個別メンタリング', desc: '講師が毎週進捗を確認し、苦手な点を個別にサポートします。' },
    ],
    schedule: {
      label: 'クラススケジュール - 2026/27年度 入学受付中',
      title: 'JLPTバッチ時間割',
      description: 'レベルを選んでから、ご都合に合わせて朝または午後のバッチをお選びください（2026/27年度・週5日開催）。',
      badge: '定員に限りがあります',
      levelTabLabel: 'JLPT',
      slots: [
        { slot: '朝クラス', time: '9:00 - 11:00', icon: 'morning' },
        { slot: '午後クラス', time: '14:00 - 16:00', icon: 'afternoon' },
      ],
      days: '週5日 ・ 2026/27年度',
      applyBtnLabel: '今すぐ申し込む',
    },
    custom: {
      label: 'その他のご要望',
      title: 'カスタムプランをご希望ですか？',
      description: '上記に希望のものがない場合はお問い合わせください。目標とスケジュールに合わせたプランをご提案します。',
      btnLabel: 'お問い合わせ',
      phone: '+92 300 1301223',
    },
  },
} as const

const HIGHLIGHT_ICONS: Record<string, typeof BadgeCheck> = { cert: BadgeCheck, job: Users, visa: ScrollText }

const ICONS = [GraduationCap, Award, Briefcase, PlaneTakeoff, Calendar, ShieldCheck]
const ICON_STYLES = [
  { text: "text-brand-charcoal", bg: "bg-brand-soft-gray", hoverBorder: "hover:border-brand-muted", ring: "group-hover:ring-brand-cool-gray" },
  { text: "text-brand-red", bg: "bg-brand-red-soft", hoverBorder: "hover:border-brand-red/40", ring: "group-hover:ring-brand-red-soft" },
  { text: "text-brand-green", bg: "bg-brand-green-soft", hoverBorder: "hover:border-brand-green/40", ring: "group-hover:ring-brand-green-soft" },
  { text: "text-brand-charcoal", bg: "bg-brand-soft-gray", hoverBorder: "hover:border-brand-muted", ring: "group-hover:ring-brand-cool-gray" },
  { text: "text-brand-red", bg: "bg-brand-red-soft", hoverBorder: "hover:border-brand-red/40", ring: "group-hover:ring-brand-red-soft" },
  { text: "text-brand-green", bg: "bg-brand-green-soft", hoverBorder: "hover:border-brand-green/40", ring: "group-hover:ring-brand-green-soft" },
]

const SCHEDULE_ICONS: Record<string, typeof Sunrise> = { morning: Sunrise, afternoon: Sunset }
const SCHEDULE_STYLES = [
  { text: "text-brand-green", bg: "bg-brand-green-soft", border: "border-brand-green/20", pill: "bg-brand-green-soft text-brand-green" },
  { text: "text-brand-red", bg: "bg-brand-red-soft", border: "border-brand-red/25", pill: "bg-brand-red-soft text-brand-red" },
]

export function IndividualsPageView({ locale }: { locale: Locale }) {
  const copy = content[locale] || content['en']
  const [batchLevel, setBatchLevel] = useState<(typeof BATCH_LEVELS)[number]>('N5')

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  }

  return (
    <div className="relative w-full py-12 md:py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <PageDecor />

      <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} className="relative max-w-6xl mx-auto space-y-16">

        <motion.header variants={itemVariants} className="group text-center space-y-5">
          <PageHeroEyebrow>{copy.label}</PageHeroEyebrow>
          <h1 className={pageHeroTitleClass}>{copy.title}</h1>
          <p className={`${pageHeroLeadClass} max-w-xl`}>{copy.description}</p>

          <div className="flex flex-wrap justify-center gap-3 pt-2">
            {copy.highlights.map((h) => {
              const HIcon = HIGHLIGHT_ICONS[h.icon]
              return (
                <a key={h.text} href={h.href} className="inline-flex items-center gap-2 bg-white px-5 py-2.5 rounded-full border border-brand-cool-gray shadow-sm text-sm font-semibold text-brand-charcoal-80 transition-all duration-200 ease-premium hover:border-brand-gold hover:text-brand-green hover:-translate-y-0.5 focus-ring">
                  <HIcon className="w-4 h-4 text-brand-green" />
                  {h.text}
                </a>
              )
            })}
          </div>

          <ClassroomPhotoStrip className="pt-6" />
        </motion.header>

        {/* JLPT Batch Schedule Section */}
        <motion.section id="class-schedule" variants={itemVariants} className="space-y-8 scroll-mt-28">
          <div className="group text-center space-y-4">
            <PageHeroEyebrow>{copy.schedule.label}</PageHeroEyebrow>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-brand-charcoal transition-colors duration-300 group-hover:text-brand-green">{copy.schedule.title}</h2>
            <p className="max-w-xl mx-auto text-brand-charcoal-75 leading-relaxed transition-colors duration-300 group-hover:text-brand-charcoal">{copy.schedule.description}</p>

            <motion.button
              type="button"
              onClick={() => openApply()}
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              className="btn-primary gap-2 mt-2 !py-2 !px-5 !text-sm"
            >
              <Sparkles className="w-4 h-4" />
              {copy.schedule.badge}
            </motion.button>
          </div>

          {/* Level tabs: N5 → N1 */}
          <div role="tablist" aria-label="JLPT level" className="mx-auto flex w-fit flex-wrap justify-center gap-1 rounded-full bg-white p-1 shadow-sm ring-1 ring-brand-cool-gray">
            {BATCH_LEVELS.map((lvl) => (
              <button
                key={lvl}
                type="button"
                role="tab"
                aria-selected={batchLevel === lvl}
                onClick={() => setBatchLevel(lvl)}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-colors duration-200 ${
                  batchLevel === lvl ? 'bg-brand-green text-white shadow' : 'text-brand-charcoal-75 hover:text-brand-charcoal'
                }`}
              >
                {copy.schedule.levelTabLabel} {lvl}
              </button>
            ))}
          </div>

          <div className="mx-auto grid max-w-2xl gap-5 sm:grid-cols-2">
            {copy.schedule.slots.map((cls, i) => {
              const Icon = SCHEDULE_ICONS[cls.icon]
              const style = SCHEDULE_STYLES[i % SCHEDULE_STYLES.length]
              return (
                <motion.div
                  key={`${batchLevel}-${cls.slot}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.06 }}
                  whileHover={{ y: -4 }}
                  className={`relative p-6 bg-white border ${style.border} rounded-3xl shadow-sm hover:shadow-xl transition-shadow duration-300 flex flex-col gap-4`}
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-2xl ${style.bg}`}>
                    <Icon className={`w-6 h-6 ${style.text}`} />
                  </div>
                  <div className="space-y-1">
                    <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${style.pill}`}>JLPT {batchLevel}</span>
                    <h3 className="text-lg font-semibold tracking-tight text-brand-charcoal pt-1">{cls.slot}</h3>
                  </div>
                  <div className="space-y-1">
                    <p className="text-brand-charcoal font-bold text-sm">{cls.time}</p>
                    <p className="text-brand-muted text-xs font-medium">{copy.schedule.days}</p>
                  </div>
                  <motion.button
                    type="button"
                    onClick={() => openApply(`jlpt-${batchLevel.toLowerCase()}`)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="btn-primary gap-2 mt-auto w-full"
                  >
                    {copy.schedule.applyBtnLabel}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </motion.button>
                </motion.div>
              )
            })}
          </div>
        </motion.section>

        {/* Divider to separate JLPT schedule section from program offerings below */}
        <div className="flex items-center gap-4 max-w-3xl mx-auto">
          <span className="flex-1 h-px bg-brand-cool-gray" />
          <span className="w-2 h-2 rounded-full bg-brand-cool-gray" />
          <span className="flex-1 h-px bg-brand-cool-gray" />
        </div>


        <motion.div id="programs" variants={containerVariants} className="grid md:grid-cols-3 gap-6 scroll-mt-28">
          {(copy.cards || []).map((card, i) => {
            const Icon = ICONS[i]
            const style = ICON_STYLES[i]
            return (
              <MotionLink
                key={card.title}
                href={i === 0 ? '#class-schedule' : `/${locale}/contact?subject=${encodeURIComponent(card.title)}`}
                variants={itemVariants}
                whileHover={{ y: -4 }}
                className={`group relative flex flex-col p-8 bg-white border border-brand-cool-gray rounded-3xl shadow-sm hover:shadow-xl ${style.hoverBorder} transition-all duration-300 focus-ring`}
              >
                <span className="absolute top-6 right-7 text-xs font-bold text-brand-cool-gray tracking-widest">0{i + 1}</span>
                <div className={`mb-6 w-14 h-14 flex items-center justify-center rounded-2xl transition-all duration-300 ${style.bg} ring-4 ring-transparent ${style.ring} group-hover:scale-105`}>
                  <Icon className={`w-7 h-7 ${style.text}`} />
                </div>
                <h3 className="text-xl font-semibold tracking-tight text-brand-charcoal mb-3">{card.title}</h3>
                <p className="flex-1 text-brand-muted text-sm leading-relaxed font-light">{card.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red">
                  {i === 0 ? copy.firstCardCta : copy.cardCta}
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </MotionLink>
            )
          })}
        </motion.div>

        <motion.div variants={itemVariants}>
          <ConsultationCTASection
            eyebrow={copy.custom.label}
            title={copy.custom.title}
            description={copy.custom.description}
            leadingIcon={<MessageCircleQuestion className="w-7 h-7 text-brand-red" />}
            actions={
              <div className="flex flex-col items-center gap-2">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    href={`/${locale}/contact`}
                    className="btn-primary gap-2 whitespace-nowrap"
                  >
                    <PhoneCall className="w-4 h-4" />
                    {copy.custom.btnLabel}
                  </Link>
                </motion.div>
                <a
                  href={`tel:${copy.custom.phone.replace(/\s+/g, '')}`}
                  className="text-brand-muted text-sm font-semibold hover:text-brand-charcoal-75 transition-colors"
                >
                  {copy.custom.phone}
                </a>
              </div>
            }
          />
        </motion.div>
      </motion.div>
    </div>
  )
}